/**
 * Origin helpers for the iframe + postMessage module bridge.
 *
 * Security model: the Shell embeds exactly one module per `<ModuleFrame>` at a
 * known base URL. Auth tokens must therefore only ever be posted to — and
 * inbound messages only ever accepted from — that module's *exact* origin.
 *
 * We never use `'*'` as a postMessage `targetOrigin` (that broadcasts the
 * session tokens to whatever origin the iframe was redirected to), and we never
 * substring-match `event.origin`. A check like `origin.includes('insolitum.ai')`
 * or `origin.startsWith(moduleUrl)` is exploitable: an attacker-controlled
 * `https://insolitum.ai.evil.com` (or `https://lexshift.insolitum.ai.evil.com`)
 * satisfies both yet is a different origin.
 */

/**
 * Resolve the canonical origin (`scheme://host[:port]`) for a module base URL.
 *
 * Returns `null` when the URL cannot be parsed, so callers can *fail closed* —
 * refusing to broadcast credentials, or to trust an inbound message, when the
 * target origin is unknown.
 */
export function resolveModuleOrigin(baseUrl: string | null | undefined): string | null {
  if (!baseUrl) return null;
  try {
    return new URL(baseUrl).origin;
  } catch {
    return null;
  }
}

/**
 * Strict gate for inbound postMessage events: an event is trusted only when it
 * originates from the exact origin this frame embeds. No substring or prefix
 * matching, no implicit wildcards.
 */
export function isTrustedModuleOrigin(eventOrigin: string, moduleOrigin: string | null): boolean {
  if (!moduleOrigin || !eventOrigin) return false;
  return eventOrigin === moduleOrigin;
}
